import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import en from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

import AppLayout from './components/AppLayout';
import ChoosePlanScreen from './components/ChoosePlanScreen';
import { LanguageProvider } from './i18n';
import useShopify from './hooks/useShopify';
import PageSkeleton from './components/ui/PageSkeleton';
// Route-level code splitting: the Dashboard (the first screen every
// session) stays in the main bundle; every other page loads its own
// chunk on first visit. The heaviest pages (Ads ~4k lines, NewProduct,
// Website) no longer weigh down initial load inside Shopify admin.
import Dashboard from './pages/Dashboard';
const NewProduct = lazy(() => import('./pages/NewProduct'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Chat = lazy(() => import('./pages/Chat'));
const Settings = lazy(() => import('./pages/Settings'));
const Ads = lazy(() => import('./pages/Ads'));
const Website = lazy(() => import('./pages/Website'));
const Advertorials = lazy(() => import('./pages/Advertorials'));
const SplitTesting = lazy(() => import('./pages/SplitTesting'));
const CreateImages = lazy(() => import('./pages/CreateImages'));
const CreatePage = lazy(() => import('./pages/CreatePage'));
const UpsellDiscovery = lazy(() => import('./pages/UpsellDiscovery'));
const FindProducts = lazy(() => import('./pages/FindProducts'));
const Invite = lazy(() => import('./pages/Invite'));
const Feedback = lazy(() => import('./pages/Feedback'));
const Referrals = lazy(() => import('./pages/Referrals'));
// Refer & Earn hub + affiliate program (application, dashboard, terms).
const ReferAndEarn = lazy(() => import('./pages/ReferAndEarn'));
const AffiliateApply = lazy(() => import('./pages/AffiliateApply'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const AffiliateTerms = lazy(() => import('./pages/AffiliateTerms'));
// Click-to-edit editor for a generation (opened from every "Previous
// generations" gallery's View / Edit).
const GenerationEditor = lazy(() => import('./pages/GenerationEditor'));
// "Get my code" target of the Autonoma AI panel inside Shopify's theme
// editor: one big 6-digit code to paste into the panel. The old popup
// route (/theme-editor-connect) shows the same page.
const ThemeEditorCode = lazy(() => import('./pages/ThemeEditorCode'));

// App Bridge note: the LATEST App Bridge is loaded from Shopify's CDN in
// public/index.html (meta shopify-api-key + app-bridge.js). It initialises
// itself from the page URL — no React provider needed — and exposes
// window.shopify, which lib/api.js uses for session tokens. Outside the
// Shopify admin (local dev, marketing redirect) the script no-ops and API
// calls 401 → install flow, same as before.

/** Provides t()/language app-wide, loading the shop's saved language. */
function LanguageRoot({ children }) {
  const { shop } = useShopify();
  return <LanguageProvider shop={shop}>{children}</LanguageProvider>;
}

export default function App() {
  return (
    <AppProvider i18n={en}>
      <BrowserRouter>
        <LanguageRoot>
          <Suspense fallback={<PageSkeleton variant="form" rows={2} />}>
          <Routes>
            {/* PUBLIC referral invite — the clean share link
                (app.tryautonoma.app/r/CODE) lands here; no session, no
                AppLayout (a visitor has no shop yet). */}
            <Route path="/r/:code" element={<Invite />} />
            {/* Theme-editor AI code page: embedded (App Bridge) but without
                the app chrome — a one-purpose page opened from the panel. */}
            <Route path="/theme-editor-code" element={<ThemeEditorCode />} />
            <Route path="/theme-editor-connect" element={<ThemeEditorCode />} />
            {/* DEV ONLY: the first-install plan picker with real API data
                and inert CTAs, so it can be screenshotted / QA'd without a
                fresh install. Compiled out of production builds. */}
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/dev/plan-picker" element={<ChoosePlanScreen preview />} />
            )}
            <Route path="/" element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="new-product" element={<NewProduct />} />
              <Route path="products" element={<Products />} />
              <Route path="products/create-images" element={<CreateImages />} />
              <Route path="products/create-page" element={<CreatePage />} />
              <Route path="product/:id" element={<ProductDetail />} />
              <Route path="website" element={<Website />} />
              <Route path="ads" element={<Ads />} />
              <Route path="advertorials" element={<Advertorials />} />
              <Route path="split-testing" element={<SplitTesting />} />
              <Route path="upsell" element={<UpsellDiscovery />} />
              <Route path="find-products" element={<FindProducts />} />
              <Route path="chat" element={<Chat />} />
              <Route path="settings" element={<Settings />} />
              {/* Settings → Referrals: share link, channels, status log, bonus credits, terms. */}
              <Route path="referrals" element={<Referrals />} />
              {/* Refer & Earn hub: peer referral card + affiliate program card. */}
              <Route path="refer-and-earn" element={<ReferAndEarn />} />
              <Route path="affiliate/apply" element={<AffiliateApply />} />
              <Route path="affiliate/dashboard" element={<AffiliateDashboard />} />
              <Route path="affiliate/terms" element={<AffiliateTerms />} />
              {/* Interactive click-to-edit preview of one generation. */}
              <Route path="generations/:id" element={<GenerationEditor />} />
              {/* Review prompt 👎 branch + general feedback form (routes to the support inbox). */}
              <Route path="feedback" element={<Feedback />} />
            </Route>
          </Routes>
          </Suspense>
        </LanguageRoot>
      </BrowserRouter>
    </AppProvider>
  );
}
